import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Item } from '../types';
import { deletePhoto } from './photoService';

// Collection reference
const itemsCollection = collection(db, 'items');

// Get a single item by ID
export const getItem = async (itemId: string): Promise<Item> => {
  const itemDoc = await getDoc(doc(db, 'items', itemId));
  
  if (!itemDoc.exists()) {
    throw new Error(`Item with ID ${itemId} not found`);
  }
  
  return {
    id: itemDoc.id,
    ...itemDoc.data()
  } as Item;
};

// Get all items for a project
export const getItemsByProject = async (projectId: string): Promise<Item[]> => {
  const itemsQuery = query(
    itemsCollection,
    where('projectId', '==', projectId),
    orderBy('priority', 'desc')
  );
  
  const itemsSnapshot = await getDocs(itemsQuery);
  return itemsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Item));
};

// Get all items for a specific room
export const getItemsByRoom = async (projectId: string, roomId: string): Promise<Item[]> => {
  const itemsQuery = query(
    itemsCollection,
    where('projectId', '==', projectId),
    where('roomId', '==', roomId),
    orderBy('priority', 'desc')
  );
  
  const itemsSnapshot = await getDocs(itemsQuery);
  return itemsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Item));
};

// Get all items without a room assigned
export const getItemsWithoutRoom = async (projectId: string): Promise<Item[]> => {
  // First get all items for the project
  const allItems = await getItemsByProject(projectId);
  
  // Filter out items that don't have a roomId
  return allItems.filter(item => !item.roomId);
};

// Create a new item
export const createItem = async (itemData: Omit<Item, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ id: string }> => {
  const timestamp = Timestamp.now();
  
  const newItem = {
    ...itemData,
    createdAt: timestamp,
    updatedAt: timestamp
  };
  
  const docRef = await addDoc(itemsCollection, newItem);
  return { id: docRef.id };
};

// Update an existing item
export const updateItem = async (
  itemId: string,
  itemData: Partial<Omit<Item, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<{ id: string }> => {
  const itemRef = doc(db, 'items', itemId);
  
  // Get the current item data to check if roomId needs to be explicitly removed
  const currentItem = await getDoc(itemRef);
  const updateData: any = { ...itemData, updatedAt: Timestamp.now() };
  
  // Handle roomId field:
  // 1. If roomId is explicitly set to empty string or undefined in the update data, set it to null
  // 2. If the current item has a roomId but the update doesn't include one, keep the existing value
  if ('roomId' in itemData && !itemData.roomId) {
    updateData.roomId = null;
  }
  
  await updateDoc(itemRef, updateData);
  
  return { id: itemId };
};

// Delete an item
export const deleteItem = async (itemId: string, projectId?: string): Promise<void> => {
  if (projectId) {
    const photosQuery = query(
      collection(db, 'photos'),
      where('projectId', '==', projectId),
      where('itemId', '==', itemId)
    );
    const photosSnap = await getDocs(photosQuery);
    await Promise.allSettled(
      photosSnap.docs.map(d => deletePhoto(d.id, projectId))
    );
  }

  await deleteDoc(doc(db, 'items', itemId));
};

// Update item pickup status
export const updateItemPickupStatus = async (
  itemId: string, 
  pickupStatus: 'pending' | 'picked' | 'left'
): Promise<{ id: string }> => {
  const itemRef = doc(db, 'items', itemId);
  
  await updateDoc(itemRef, {
    pickupStatus,
    updatedAt: Timestamp.now()
  });
  
  return { id: itemId };
};

// Get item counts for all rooms in a project
export const getItemCountsByRooms = async (projectId: string): Promise<Record<string, number>> => {
  const itemsQuery = query(
    itemsCollection,
    where('projectId', '==', projectId)
  );
  
  const itemsSnapshot = await getDocs(itemsQuery);
  const roomCounts: Record<string, number> = {};
  
  itemsSnapshot.docs.forEach(doc => {
    const item = doc.data();
    if (item.roomId) {
      roomCounts[item.roomId] = (roomCounts[item.roomId] || 0) + 1;
    } else {
      // Count items without a room in a special "unassigned" category
      roomCounts['unassigned'] = (roomCounts['unassigned'] || 0) + 1;
    }
  });
  
  return roomCounts;
}; 